/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: Prop types for checking props type.
import Moment from 'moment' // Npm: Moment.js library for date and time formatting.
import _ from 'underscore' // Npm: utility module.
import { HiPlusCircle } from 'react-icons/hi2' // Npm: React icons.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useQuery } from '@apollo/client' // Npm: Apollo client for GraphQL queries and mutations.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Text,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import DownloadToExcel from 'components/DownloadToExcel'
import Modal from 'components/Modal'
import VendorAccountAttachPhoneNumberoRoutePlan from 'components/VendorAccountAttachPhoneNumberoRoutePlan'
import { MemoizedInput } from 'components/MemoizedInput'
import PhoneNumberoRoutePlanUpsert from 'components/PhoneNumberoRoutePlanUpsert'
import PhoneNumberoRoutePlanDelete from 'components/PhoneNumberoRoutePlanDelete'
import PhoneNumberoRoutePlanDropDownOptions from 'components/PhoneNumberoRoutePlanDropDownOptions'

/*
 * GRAPHS
 */
import PhoneNumberoRoutePlanReadQuery from './__query__/index.phonenumberoroutePlan.read.query'


/*
 * STYLES
 */
import { buttonStyle } from './index.style'



/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Local variable.
  let _rows

  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [selectAll, setSelectAll] = React.useState(false)
  const [selectedIds, setSelectedIds] = React.useState([])
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 8,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({
    'items': [],
    'logicOperator': 'and',
    'quickFilterValues': [],
    'quickFilterLogicOperator': 'and'
  })

  // Debounced search states
  const [mccNameToSearch, setMccNameToSearch] = React.useState('')
  const [mncNameToSearch, setMncNameToSearch] = React.useState('')
  const [countryNameToSearch, setCountryNameToSearch] = React.useState('')
  const [operatorNameToSearch, setOperatorNameToSearch] = React.useState('')

  const { 'isOpen': isRoutePlanCreateOpen, 'onOpen': onRoutePlanCreateOpen, 'onClose': onRoutePlanCreateClose } = useDisclosure()
  const { 'isOpen': isRoutePlanUpdateOpen, 'onOpen': onRoutePlanUpdateOpen, 'onClose': onRoutePlanUpdateClose } = useDisclosure()
  const { 'isOpen': isRoutePlanDeleteOpen, 'onOpen': onRoutePlanDeleteOpen, 'onClose': onDeleteModalClose } = useDisclosure()
  const { 'isOpen': isVendorAccountAttachRoutePlanOpen, 'onOpen': onVendorAccountAttachRoutePlanOpen, 'onClose': onVendorAccountAttachRoutePlanClose } = useDisclosure()

  // Debounce handlers
  const _onMccNameToSearchInputChange = React.useRef(_.debounce((e) => setMccNameToSearch(e.target.value), 800)).current
  const _onMncNameToSearchInputChange = React.useRef(_.debounce((e) => setMncNameToSearch(e.target.value), 800)).current
  const _onOperatorNameToSearchInputChange = React.useRef(_.debounce((e) => setOperatorNameToSearch(e.target.value), 800)).current
  const _onCountryNameToSearchInputChange = React.useRef(_.debounce((e) => setCountryNameToSearch(e.target.value), 800)).current

  // Cleanup on unmount
  const _setFilterDebounced = React.useMemo(() => _.debounce(setFilterModel, 500), [])

  React.useEffect(() => {
    return () => {
      _onMccNameToSearchInputChange.cancel?.()
      _onMncNameToSearchInputChange.cancel?.()
      _onOperatorNameToSearchInputChange.cancel?.()
      _onCountryNameToSearchInputChange.cancel?.()
      _setFilterDebounced.cancel?.()
    }
  }, [])

  const _isFilterApplied = filterModel?.items?.some(i => i.value != void 0 && i.value !== '')
  const _appliedFilter = _isFilterApplied ? filterModel : void 0

  const _PhoneNumberoQueryRoutePlanRead = useQuery(PhoneNumberoRoutePlanReadQuery, {
    'variables': {
      'routeId': passOn?.routeId,
      'displayName': passOn?.routeId?.displayName,
      mccNameToSearch,
      mncNameToSearch,
      operatorNameToSearch,
      countryNameToSearch,
      'filter': _appliedFilter,
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy
  })

  const { refetch } = _PhoneNumberoQueryRoutePlanRead

  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched
    if (_PhoneNumberoQueryRoutePlanRead.data) {
      // Reset states.
      setSelectedIds([])
      setSelectAll(false)
    }
  }, [
    isRoutePlanCreateOpen,
    isRoutePlanUpdateOpen,
    isRoutePlanDeleteOpen,
    isVendorAccountAttachRoutePlanOpen,
    _PhoneNumberoQueryRoutePlanRead.data
  ])

  // Object assignment.
  const _HandleCheckboxChange = id => {
    // Update state.
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }
  const _HandleSelectAll = () => {
    // Update state.
    setSelectAll(!selectAll)
    setSelectedIds(selectAll ? [] : _PhoneNumberoQueryRoutePlanRead?.data?.PhoneNumberoRoutePlanRead?.map(item => item.id) || [])
  }

  // Data assignment.
  if (!_PhoneNumberoQueryRoutePlanRead.loading && 0 < _PhoneNumberoQueryRoutePlanRead.data?.PhoneNumberoRoutePlanRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _PhoneNumberoQueryRoutePlanRead.data?.PhoneNumberoRoutePlanRead?.length || (0 < _PhoneNumberoQueryRoutePlanRead.data?.PhoneNumberoRoutePlanRead?.length && (!_.every(_.pluck(_PhoneNumberoQueryRoutePlanRead.data?.PhoneNumberoRoutePlanRead, 'status'), i => _successFlags?.includes(i)) || _.some(_.pluck(_PhoneNumberoQueryRoutePlanRead.data?.PhoneNumberoRoutePlanRead, 'status'), i => 'ROUTE_PLAN_NOT_FOUND' === i)))) _isFirstLoadCompleted.current = false

  // Assign rows
  _rows = _PhoneNumberoQueryRoutePlanRead.data?.PhoneNumberoRoutePlanRead

  // Columns definition
  const columns = React.useMemo(() => [
    {
      field: 'checkbox',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox bg='gray.300' onChange={_HandleSelectAll} isChecked={selectAll} />
      ),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
          <Checkbox
            bg='gray.300'
            isChecked={selectedIds.includes(row.id)}
            onChange={() => _HandleCheckboxChange(row.id)}
          />
        </Box>
      )
    },
    {
      'field': 'sno',
      'headerName': 'S No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'routeName',
      'headerName': 'Route Name',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'vendorAccount',
      'headerName': 'Vendor Account',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'mcc',
      'headerName': 'Mcc',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150
    },
    {
      'field': 'mnc',
      'headerName': 'Mnc',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150
    },
    {
      'field': 'operator',
      'headerName': 'Operator',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 250
    },
    {
      'field': 'countryName',
      'headerName': 'Country Name',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'dialingCode',
      'headerName': 'Dialing Code',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 120
    },
    {
      'field': 'phoneNumberoDirectory',
      'headerName': 'PhoneNumbero Folder',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'phoneNumbero',
      'headerName': 'PhoneNumberos',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'createdAt',
      'headerName': 'CreatedAt',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 180
    },
    {
      'field': 'updatedAt',
      'headerName': 'UpdatedAt',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 180
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      'renderCell': ({ row }) => (
        <PhoneNumberoRoutePlanDropDownOptions
          routePlanIds={[...selectedIds, row.id]}
          routePlanId={row.id}
          routeId={row.routeId}
          onRoutePlanUpdate={onRoutePlanUpdateOpen}
          onRoutePlanDelete={onRoutePlanDeleteOpen}
          onVendorAccountAttachRoutePlanOpen={onVendorAccountAttachRoutePlanOpen}
          selectedIds={selectedIds}
        />
      )
    }
  ], [selectAll, selectedIds])

  // Rows mapping - with successFlags validation
  const rows = React.useMemo(() =>
    _.compact(
      _rows?.map((item, index) =>
        _successFlags.includes(item?.status)
          ? {
            'id': item?.id || String.random(9),
            'routeId': item?.PhoneNumberoRoute?.id,
            'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
            'routeName': item?.PhoneNumberoRoute?.displayName ?? '-',
            'vendorAccount': item?.VendorAccount?.displayName ?? '-',
            'mcc': item?.Mcc?.mcc ?? '-',
            'mnc': item?.Mnc?.mnc ?? '-',
            'operator': item?.Mnc?.network ?? '-',
            'countryName': item?.Mcc?.countryName ?? '-',
            'dialingCode': item?.Mcc?.dialingCode ?? '-',
            'phoneNumberoDirectory': item?.PhoneNumberoDirectory?.displayName ?? '-',
            'phoneNumbero': item?.PhoneNumbero?.map?.(j => j.phoneNumbero)?.join(', ') || '-',
            'createdAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
            'updatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD')
          }
          : null
      )
    ) || [],
    [_rows, paginationModel.page, paginationModel.pageSize]
  )


  // Return component.
  return (
    <>
      <Flex className='phonenumberoroutePlan base' >
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '14px'}
          bg='white'
          height='100%'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>

          {/* Header Section with Buttons */}
          <Flex pb='0px' justify='space-between' alignItems='center'>
            <Flex display='flex' flexDir='row' gap='12px'>
              <Button
                onClick={onRoutePlanCreateOpen}
                leftIcon={<HiPlusCircle />}
                style={buttonStyle}>
                New PhoneNumbero Route Plan
              </Button>
            </Flex>
            <DownloadToExcel
              cellsData={0 === _PhoneNumberoQueryRoutePlanRead.data?.PhoneNumberoRoutePlanRead?.length || !_.every(_.pluck(_PhoneNumberoQueryRoutePlanRead.data?.PhoneNumberoRoutePlanRead, 'status'), i => _successFlags?.includes(i)) ? [] : _PhoneNumberoQueryRoutePlanRead.data?.PhoneNumberoRoutePlanRead?.map((item, __index) => ({
                'S.No.': `${(__index + (paginationModel.pageSize * paginationModel.page)) + 1}.`,
                'Route Name': item.PhoneNumberoRoute?.displayName ?? '-',
                'Vendor Account': item.VendorAccount?.displayName ?? '-',
                'Mcc': item?.Mcc?.mcc ?? '-',
                'Mnc': item?.Mnc?.mnc ?? '-',
                'Operator': item?.Mnc?.network ?? '-',
                'Country Name': item?.Mcc?.countryName ?? '-',
                'Dialing Code': item?.Mcc?.dialingCode ?? '-',
                'PhoneNumbero Folder': item?.PhoneNumberoDirectory?.displayName ?? '-',
                'PhoneNumberos': item?.PhoneNumbero?.map?.(j => j.phoneNumbero)?.join(', ') || '-',
                'CreatedAt': Moment(item.createdAt).format('YYYY-MM-DD'),
                'UpdatedAt': Moment(item.updatedAt).format('YYYY-MM-DD')
              }))}
              headersData={[
                'S.No.',
                'Route Name',
                'Vendor Account',
                'Mcc',
                'Mnc',
                'Operator',
                'Country Name',
                'Dialing Code',
                'PhoneNumbero Folder',
                'PhoneNumberos',
                'CreatedAt',
                'UpdatedAt'
              ].map(i => ({ 'key': i, 'label': i }))} />
          </Flex>

          {/* Filter Section */}
          <Flex
            gap='12px'
            flexWrap='wrap'
            bg='white'
            p='10px'
            borderRadius='12px'
            border='1px solid #C5CFE8'
            alignItems='flex-start'
          >
            <Box
              minW={{ base: '100%', md: '150px' }}
              w={{ base: '100%', md: '200px' }}
            >
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Mcc</Text>
              <MemoizedInput
                placeholder='Search Mcc...'
                defaultValue={mccNameToSearch}
                onChange={_onMccNameToSearchInputChange}
              />
            </Box>

            <Box
              minW={{ base: '100%', md: '150px' }}
              w={{ base: '100%', md: '200px' }}
            >
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Mnc</Text>
              <MemoizedInput
                placeholder='Search Mnc...'
                defaultValue={mncNameToSearch}
                onChange={_onMncNameToSearchInputChange}
              />
            </Box>

            <Box
              minW={{ base: '100%', md: '150px' }}
              w={{ base: '100%', md: '200px' }}
            >
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Country Name</Text>
              <MemoizedInput
                placeholder='Search Country Name...'
                defaultValue={countryNameToSearch}
                onChange={_onCountryNameToSearchInputChange}
              />
            </Box>

            <Box
              minW={{ base: '100%', md: '150px' }}
              w={{ base: '100%', md: '200px' }}
            >
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Operator</Text>
              <MemoizedInput
                placeholder='Search Operator...'
                defaultValue={operatorNameToSearch}
                onChange={_onOperatorNameToSearchInputChange}
              />
            </Box>
          </Flex>
          <Flex
            flex={1}
            flexDirection="column"
            border="1px solid #B6BED4"
            borderBottomRadius="15px"
            borderTopRadius="15px"
            overflow="hidden"
            minH={0}
            width="100%"
          >

            <DataGrid
              key={`${paginationModel.page}-${paginationModel.pageSize}`}
              rows={rows}
              columns={columns}
              rowHeight={58}
              rowCount={_.first(_PhoneNumberoQueryRoutePlanRead?.data?.PhoneNumberoRoutePlanRead)?._totalCount ?? 0}
              paginationMode='server'
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              filterMode='server'
              filterModel={filterModel}
              onFilterModelChange={_setFilterDebounced}
              pageSizeOptions={[8, 20, 50, 100]}
              slots={{
                'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
              }}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
              loading={_PhoneNumberoQueryRoutePlanRead?.loading}
              disableColumnMenu={false}
              sx={{
                '& .MuiDataGrid-main': {
                  border: 'none',
                  borderRadius: 0
                }
              }}
            />
          </Flex>
        </Flex>

        {/* Modals */}
        <Modal
          size='3xl'
          title='PhoneNumbero Route Plan Create'
          isOpen={isRoutePlanCreateOpen}
          onClose={onRoutePlanCreateClose}
          isCentered={false}>
          <PhoneNumberoRoutePlanUpsert refetch={refetch} isCreateOnly={true} onClose={onRoutePlanCreateClose} />
        </Modal>

        <Modal
          size='3xl'
          title='PhoneNumbero Route Plan Update'
          isOpen={isRoutePlanUpdateOpen}
          onClose={onRoutePlanUpdateClose}
          isCentered={false}>
          <PhoneNumberoRoutePlanUpsert refetch={refetch} isCreateOnly={false} onClose={onRoutePlanUpdateClose} />
        </Modal>

        <Modal
          size='md'
          title='PhoneNumbero Route Plan Delete'
          isOpen={isRoutePlanDeleteOpen}
          onClose={onDeleteModalClose}>
          <PhoneNumberoRoutePlanDelete refetch={refetch} onClose={onDeleteModalClose} />
        </Modal>

        <Modal
          size='md'
          title='Attach Vendor Account'
          isOpen={isVendorAccountAttachRoutePlanOpen}
          onClose={onVendorAccountAttachRoutePlanClose}>
          <VendorAccountAttachPhoneNumberoRoutePlan refetch={refetch} onClose={onVendorAccountAttachRoutePlanClose} />
        </Modal>

      </Flex>
    </>
  )
}

/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}

/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
